import React, { Component } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import DocumentTitle from '../../components/DocumentTitle';
import { signInUser } from '../../actions/auth';
import { getIntendedUrl } from '../../helpers/auth';
import FormError from '../../components/partials/FormError';
import __ from '../../helpers/translation';
import packageJson from '../../../../package.json'
import { destructServerErrors, hasError, getError } from '../../helpers/error';
import { CloudDownloadIcon, LogInIcon } from 'lucide-react';

const propTypes = {
  signInUser: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired
};

class SignIn extends Component {

  constructor (props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      errors: '',
      showResetDialog: false,
    };
  }

  signInSuccess () {
    this.props.history.push('/home')
  }

  handleSubmit (e) {
    e.preventDefault();
    this.props.signInUser(this.state)
      .then(response => this.signInSuccess())
      .catch(error => this.setState({ errors: destructServerErrors(error) }));
  }

  handleInputChange (e) {
    this.setState({
      [e.target.name]: e.target.value,
      errors: {
        ...this.state.errors,
        ...{ [e.target.name]: '' }
      }
    });
  }

  componentDidMount () {
    document.body.classList.add('bg-green-gradient','d-flex', 'flex-column', 'mh-100vh', 'justify-content-center');
  }

  componentWillUnmount () {
    document.body.classList.remove(... document.body.classList);
  }

  async resetClientData() {
    try {
      localStorage.clear();
      sessionStorage.clear();
      if(indexedDB.databases) {
        const dbs = await indexedDB.databases();
        for(const db of dbs) {
          if(db.name) indexedDB.deleteDatabase(db.name);
        }
      }
      if('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let reg of registrations) {
          await reg.unregister();
        }
      }
    }
    catch(err) {
      console.error(err);
    }
    finally {
      const baseUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
      const newUrl = baseUrl + '?nocache=' + new Date().getTime();
      window.location.href = newUrl;
    }
  }

  render () {
    return (
      <DocumentTitle title={`${__('app.login.title')} - ${window.App.name}`}>

        <div className="container">
          <div className="row">
              
            <div className="col-12 text-center my-5">
              <img 
                src={window.App.baseUrl + "assets/app/img/logo_sloganless.png"} 
                alt="Logo"
                className="img-fluid p-0-50 mw-sm-400" 
              />
            </div>
            
            <div className="col-md-6 offset-md-3 col-12">
              <form autoComplete="off" onSubmit={e => this.handleSubmit(e)} method="POST">
                
                {/* Username field */}
                <div className="form-label-group">
                  <input 
                    value={this.state.email}
                    onChange={e => this.handleInputChange(e)}
                    name="email"
                    type="text" 
                    id="inputEmail" 
                    className="form-control" 
                    placeholder={__('app.login.email')} 
                    required 
                    autoFocus 
                  />
                  <label htmlFor="inputEmail">
                    {__('app.login.email')}
                  </label>
                  {hasError(this.state.errors, 'email') && (
                    <div className="mt-3">
                      <FormError>{getError(this.state.errors, 'email')}</FormError>
                    </div>
                  )}
                </div>
                
                {/* Password field */}
                <div className="form-label-group">
                  <input
                    value={this.state.password}
                    onChange={e => this.handleInputChange(e)}
                    type="password"
                    id="inputPassword"
                    name="password"
                    className="form-control"
                    placeholder={ __('app.login.password') }
                    required 
                  />
                  <label htmlFor="inputPassword">
                    {__('app.login.password')}
                  </label>
                </div>

                {/* Submit button */}
                <input 
                  type="submit" 
                  className="btn btn-black d-block w-100 light-shadow mb-3" 
                  value={__('app.login.button')}
                />

                <button
                  className='btn btn-dark d-block w-100 light-shadow mb-3'
                  onClick={() => this.setState({ showResetDialog: true })}
                  type='button'
                >
                  <CloudDownloadIcon className='tw-size-5 tw-mr-3 tw-inline-block tw-relative tw-bottom-[2px]'/>
                  {__('app.login.retrieve_newest_version')}
                </button>

                {/* Forgot password link */}
                <Link to="/forgot-password" className="btn btn-link text-white text-center w-100">
                  {__('app.login.reset_password')}
                </Link>

              </form>
            </div>
            
            <div 
              className="col-12 text-center my-5" 
              dangerouslySetInnerHTML={{__html: __('app.login.user_agreement')}} 
            />
          </div>
        </div>

        <div className="tw-absolute tw-bottom-2 tw-right-2">
          <p className="tw-font-semibold tw-my-0 tw-text-sm tw-text-right">
            v{packageJson.version}
          </p>
        </div>
        
        {/* Reset dialog with disclaimer */}
        {this.state.showResetDialog && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-isolate tw-bg-neutral-900/50">
            <div className="tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md tw-mx-4 tw-w-full tw-max-w-[30rem] tw-z-10">
              <h1 className="tw-text-2xl tw-font-semibold">
                {__('app.login.retrieve_newest_version_confirm_title')}
              </h1>

              <p className="tw-text-md tw-mt-2 tw-text-neutral-500">
                {__('app.login.retrieve_newest_version_confirm_description')}
              </p>

              <div className="tw-mt-8 tw-flex tw-gap-x-6">
                <button 
                  className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-neutral-200 tw-text-neutral-900 tw-text-lg tw-font-semibold hover:tw-bg-neutral-300 tw-w-full"
                  onClick={() => this.setState({ showResetDialog: false })}
                >
                  {__('app.login.retrieve_newest_version_cancel_button')}
                </button>
                <button 
                  className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-neutral-900 tw-text-white tw-text-lg tw-font-semibold hover:tw-bg-neutral-800 tw-w-full" 
                  onClick={() => this.resetClientData()}
                >
                  {__('app.login.retrieve_newest_version_confirm_button')}
                </button>
              </div>
            </div>
          </div>
        )}

      </DocumentTitle>
    );
  }
}

SignIn.propTypes = propTypes;

const mapDispatchToProps = {
  signInUser
};

export default connect(null, mapDispatchToProps)(withRouter(SignIn));
